import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Music, MapPin, ArrowRight, ArrowLeft, Check, Loader2, Upload } from 'lucide-react';
import { API } from '../../config/api';



const GENRES = [
  'Hip Hop', 'R&B', 'Go-Go', 'Jazz', 'Rock', 'Electronic', 'Gospel',
  'Neo-Soul', 'Trap', 'Drill', 'Pop', 'Indie', 'Alternative', 'Reggae', 'Afrobeats'
];

const REGIONS = [
  { value: 'dc', label: 'Washington DC' },
  { value: 'md', label: 'Maryland' },
  { value: 'va', label: 'Virginia' }
];

export default function ArtistOnboarding() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [profile, setProfile] = useState({
    stage_name: '',
    location: '',
    region: '',
    genres: [],
    bio: '',
    instagram: '',
    twitter: '',
    soundcloud: '',
    spotify: ''
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user?.role && user.role !== 'artist' && user.role !== 'owner') {
      navigate(`/${user.role}/dashboard`);
    }
    
    // Pre-fill name
    if (user?.name) {
      setProfile(prev => ({ ...prev, stage_name: user.name }));
    }
  }, [isAuthenticated, authLoading, user]);

  const toggleGenre = (genre) => {
    setProfile(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre].slice(0, 3) // Max 3 genres
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      await fetch(`${API}/artist/profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          stage_name: profile.stage_name,
          neighborhood: profile.location,
          region: profile.region,
          genres: profile.genres,
          bio: profile.bio,
          socials: {
            instagram: profile.instagram,
            twitter: profile.twitter,
            soundcloud: profile.soundcloud,
            spotify: profile.spotify
          }
        })
      });
      
      await refreshUser();
      toast.success('Profile updated!');
      navigate('/artist/dashboard');
    } catch (err) {
      console.error('Onboarding error:', err);
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/artist/dashboard');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="artist-onboarding">
      <div className="max-w-lg mx-auto">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full ${step >= s ? 'bg-secondary' : 'bg-muted/50'}`} />
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div>
            <div className="w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
              <Music className="w-8 h-8 text-secondary" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-center mb-2">Set Up Your Artist Profile</h1>
            <p className="text-muted-foreground text-center mb-8">Let's get your profile ready for the DMV scene</p>
            
            <div className="space-y-4">
              <div>
                <Label>Stage Name</Label>
                <Input
                  value={profile.stage_name}
                  onChange={(e) => setProfile({ ...profile, stage_name: e.target.value })}
                  placeholder="Your artist name"
                />
              </div>
              
              <div>
                <Label>Region</Label>
                <Select value={profile.region} onValueChange={(v) => setProfile({ ...profile, region: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your region" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>City/Neighborhood</Label>
                <Input
                  value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  placeholder="e.g., Silver Spring, U Street"
                />
              </div>
            </div>

            <Button onClick={() => setStep(2)} className="w-full mt-6">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Genres */}
        {step === 2 && (
          <div>
            <h1 className="font-heading text-2xl font-bold mb-2">Select Your Genres</h1>
            <p className="text-muted-foreground mb-6">Choose up to 3 genres that best describe your sound</p>
            
            <div className="flex flex-wrap gap-2 mb-8">
              {GENRES.map((genre) => (
                <button
                  key={genre}
                  onClick={() => toggleGenre(genre)}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    profile.genres.includes(genre)
                      ? 'bg-secondary text-black'
                      : 'bg-muted/30 hover:bg-muted/50'
                  }`}
                >
                  {genre}
                  {profile.genres.includes(genre) && <Check className="w-3 h-3 ml-1 inline" />}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button onClick={() => setStep(3)} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Bio & Socials */}
        {step === 3 && (
          <div>
            <h1 className="font-heading text-2xl font-bold mb-2">Tell Your Story</h1>
            <p className="text-muted-foreground mb-6">Add a bio and connect your socials</p>
            
            <div className="space-y-4">
              <div>
                <Label>Bio</Label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="Tell fans about yourself..."
                  rows={4}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Instagram</Label>
                  <Input
                    value={profile.instagram}
                    onChange={(e) => setProfile({ ...profile, instagram: e.target.value })}
                    placeholder="@handle"
                  />
                </div>
                <div>
                  <Label>Twitter/X</Label>
                  <Input
                    value={profile.twitter}
                    onChange={(e) => setProfile({ ...profile, twitter: e.target.value })}
                    placeholder="@handle"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Spotify</Label>
                  <Input
                    value={profile.spotify}
                    onChange={(e) => setProfile({ ...profile, spotify: e.target.value })}
                    placeholder="Spotify link"
                  />
                </div>
                <div>
                  <Label>SoundCloud</Label>
                  <Input
                    value={profile.soundcloud}
                    onChange={(e) => setProfile({ ...profile, soundcloud: e.target.value })}
                    placeholder="SoundCloud link"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button variant="outline" onClick={handleSkip}>
                Skip for now
              </Button>
              <Button onClick={handleComplete} disabled={loading} className="flex-1">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Complete Setup
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
